'use client'

import { Card, CardContent } from '@/components/ui/card'
import { SongArtwork } from '@/components/ui/song-artwork'
import { Music, History } from 'lucide-react'
import { SiApplemusic } from 'react-icons/si'
import { useTranslations } from 'next-intl'
import type { DemoNowPlaying, DemoPlayedSong } from '../demo-types'

// Song link button (Apple Music) - only shown for now playing
function SongLinks({ prominent = false }: { prominent?: boolean }) {
  if (prominent) {
    return (
      <div className="flex items-center gap-1.5 mt-2">
        <button className="flex items-center justify-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all border bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 border-pink-500/30">
          <SiApplemusic className="h-3.5 w-3.5" />
          <span>Apple Music</span>
        </button>
      </div>
    )
  }

  return null
}

interface NowPlayingSectionProps {
  nowPlaying: DemoNowPlaying | null
  lastPlayed: DemoPlayedSong | null
  onOpenHistory: () => void
}

export function NowPlayingSection({ nowPlaying, lastPlayed, onOpenHistory }: NowPlayingSectionProps) {
  const t = useTranslations('demo')
  if (!nowPlaying && !lastPlayed) return null

  return (
    <Card data-coach="audience-now-playing" className="border-violet-500/30 bg-gradient-to-r from-violet-950/50 to-fuchsia-950/50 overflow-hidden">
      <CardContent className="p-0">
        {/* Now Playing */}
        {nowPlaying && (
          <div className="p-4">
            <p className="text-xs text-violet-400 mb-2 flex items-center gap-1">
              <Music className="h-3 w-3" />
              {t('nowPlaying')}
            </p>
            <div className="flex items-start gap-3">
              <div className="relative shrink-0">
                <SongArtwork
                  src={nowPlaying.song.artworkUrl}
                  alt={nowPlaying.song.title}
                  size={64}
                  className="h-16 w-16 rounded-lg shadow-lg"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-lg leading-tight">{nowPlaying.song.title}</p>
                <p className="text-sm text-zinc-400">{nowPlaying.song.artist}</p>
                <SongLinks prominent />
              </div>
            </div>
          </div>
        )}

        {/* Previous / Last Played */}
        {lastPlayed && (
          <button
            onClick={onOpenHistory}
            className={`w-full flex items-center gap-2 px-4 text-left hover:bg-zinc-800/50 transition-colors ${
              nowPlaying ? 'py-2 bg-zinc-900/50 border-t border-zinc-800' : 'py-3'
            }`}
          >
            <History className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
            <p className="text-[10px] text-zinc-500 uppercase shrink-0">
              {nowPlaying ? t('prev') : t('lastPlayed')}
            </p>
            <SongArtwork
              src={lastPlayed.song.artworkUrl}
              alt={lastPlayed.song.title}
              size={32}
              className={`rounded shrink-0 ${nowPlaying ? 'h-8 w-8 opacity-60 grayscale' : 'h-10 w-10 opacity-70'}`}
            />
            <p className={`truncate flex-1 min-w-0 ${nowPlaying ? 'text-xs text-zinc-500' : 'text-sm font-medium text-zinc-300'}`}>
              {lastPlayed.song.title}
              {!nowPlaying && <span className="text-zinc-500 font-normal"> &bull; {lastPlayed.song.artist}</span>}
            </p>
          </button>
        )}
      </CardContent>
    </Card>
  )
}
